// shared/components/Layout/TopBar.tsx
import { useAuth } from '@/shared/auth';
import { resolveTenantLabel } from '@/shared/auth/tenantLabel';
import { useNavigate } from 'react-router-dom';
import { LogOut, LogIn } from 'lucide-react';

export default function TopBar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const isGenericIdentity = (value: unknown) => {
    const v = String(value ?? '').trim().toLowerCase();
    return !v || v === 'pac account' || v === 'signed in user' || v === 'oidc_user';
  };

  const linkedProviderEmail = Array.isArray(user?.linked_providers)
    ? user.linked_providers
      .map((provider: any) => String(provider?.email_at_link ?? '').trim())
      .find((email: string) => email.includes('@')) || null
    : null;

  const tenantLabel = resolveTenantLabel(user?.tenant_name, user?.tenantName);

  const primaryCandidate =
    tenantLabel ||
    user?.full_name ||
    user?.display_name ||
    user?.name ||
    user?.email ||
    user?.username ||
    user?.user_name ||
    user?.login ||
    null;

  const primaryIdentity =
    isGenericIdentity(primaryCandidate) && linkedProviderEmail
      ? linkedProviderEmail
      : primaryCandidate;

  const secondaryIdentityRaw =
    (tenantLabel ? (user?.full_name || user?.display_name || user?.name || user?.email) : user?.email) ||
    linkedProviderEmail ||
    user?.username ||
    user?.user_name ||
    user?.login ||
    null;

  const secondaryIdentity =
    secondaryIdentityRaw && secondaryIdentityRaw !== primaryIdentity
      ? secondaryIdentityRaw
      : null;

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      navigate('/login', { replace: true });
    }
  };

  const handleLogin = () => {
    navigate('/?login=1');
  };

  return (
    <header className="fixed top-2 right-2 z-50">
      {/* Transparent glass card — only backdrop blur + border, no bg overlay */}
      <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg border border-neutral-700/40 backdrop-blur-xl">
        {user ? (
          <>
            <div className="text-xs text-right">
              <div className="text-neutral-200 font-medium tracking-tight">{primaryIdentity || 'Signed in user'}</div>
              <div className="text-neutral-500 text-[10px]">{secondaryIdentity || 'PAC account'}</div>
            </div>
            <div className="w-px h-6 bg-neutral-700/30" />
            <button
              onClick={handleLogout}
              className="p-1.5 rounded-md hover:bg-white/[.08] transition-colors active:scale-95"
              title="Log out"
            >
              <LogOut size={14} className="text-neutral-400 hover:text-neutral-300" />
            </button>
          </>
        ) : (
          <>
            <span className="text-xs text-neutral-400 tracking-tight">visitor · read-only</span>
            <button
              onClick={handleLogin}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-md hover:bg-white/[.12] border border-neutral-700/40 text-xs text-neutral-300 transition-all active:scale-95 font-medium"
            >
              <LogIn size={12} />
              sign in
            </button>
          </>
        )}
      </div>
    </header>
  );
}